// Google Analytics (GA4) Event Tracking Utility
// This utility provides functions for tracking custom events, conversions, and e-commerce

declare global {
  interface Window {
    gtag?: (command: string, targetId: string | Date, config?: Record<string, any>) => void;
    dataLayer?: any[];
  }
}

/**
 * Check if Google Analytics is available
 */
export function isAnalyticsAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Track a custom event in Google Analytics
 * @param eventName - Name of the event
 * @param eventParams - Event parameters
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  if (!isAnalyticsAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event:', eventName, eventParams);
    }
    return;
  }

  window.gtag!('event', eventName, {
    ...eventParams,
    event_category: eventParams?.event_category || 'General',
    event_label: eventParams?.event_label || eventName,
  });
}

/**
 * Track page views
 * @param pagePath - Path of the page
 * @param pageTitle - Title of the page
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!isAnalyticsAvailable()) {
    return;
  }

  window.gtag!('config', 'G-KVLS5RYWKC', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

/**
 * Track conversion events (for conversion goals)
 * @param conversionName - Name of the conversion
 * @param value - Value of the conversion (optional)
 * @param currency - Currency code (default: 'USD')
 */
export function trackConversion(
  conversionName: string,
  value?: number,
  currency: string = 'USD'
): void {
  trackEvent('conversion', {
    event_category: 'Conversion',
    event_label: conversionName,
    value: value,
    currency: currency,
    send_to: 'G-KVLS5RYWKC',
  });
}

/**
 * Track e-commerce purchase
 * @param transactionId - Unique transaction ID
 * @param value - Transaction value
 * @param currency - Currency code (default: 'USD')
 * @param items - Array of purchased items
 */
export function trackPurchase(
  transactionId: string,
  value: number,
  currency: string = 'USD',
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>
): void {
  if (!isAnalyticsAvailable()) {
    return;
  }

  // Track purchase event
  window.gtag!('event', 'purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items || [],
  });
}

/**
 * Track add to cart event
 * @param itemId - Product/item ID
 * @param itemName - Product/item name
 * @param price - Item price
 * @param quantity - Quantity added
 * @param currency - Currency code (default: 'USD')
 */
export function trackAddToCart(
  itemId: string,
  itemName: string,
  price: number,
  quantity: number = 1,
  currency: string = 'USD'
): void {
  trackEvent('add_to_cart', {
    event_category: 'E-commerce',
    event_label: itemName,
    value: price * quantity,
    currency: currency,
    items: [
      {
        item_id: itemId,
        item_name: itemName,
        price: price,
        quantity: quantity,
      },
    ],
  });
}

/**
 * Track form submissions
 * @param formName - Name/ID of the form
 * @param formLocation - Location where form was submitted
 */
export function trackFormSubmission(formName: string, formLocation?: string): void {
  trackEvent('form_submission', {
    event_category: 'Form',
    event_label: formName,
    form_location: formLocation || window.location.pathname,
  });
}

/**
 * Track button clicks
 * @param buttonName - Name/label of the button
 * @param buttonLocation - Location where button was clicked
 */
export function trackButtonClick(buttonName: string, buttonLocation?: string): void {
  trackEvent('button_click', {
    event_category: 'Engagement',
    event_label: buttonName,
    button_location: buttonLocation || window.location.pathname,
  });
}

/**
 * Track file downloads
 * @param fileName - Name of the downloaded file
 * @param fileType - Type of the file
 */
export function trackDownload(fileName: string, fileType?: string): void {
  trackEvent('file_download', {
    event_category: 'Download',
    event_label: fileName,
    file_type: fileType,
  });
}

/**
 * Track video plays
 * @param videoTitle - Title of the video
 * @param videoDuration - Duration of the video in seconds
 */
export function trackVideoPlay(videoTitle: string, videoDuration?: number): void {
  trackEvent('video_play', {
    event_category: 'Video',
    event_label: videoTitle,
    video_duration: videoDuration,
  });
}

/**
 * Track search queries
 * @param searchTerm - Search query
 * @param resultCount - Number of results
 */
export function trackSearch(searchTerm: string, resultCount?: number): void {
  trackEvent('search', {
    event_category: 'Search',
    event_label: searchTerm,
    search_term: searchTerm,
    result_count: resultCount,
  });
}

/**
 * Track newsletter signups
 * @param email - Email address (optional, for privacy)
 */
export function trackNewsletterSignup(email?: string): void {
  trackEvent('newsletter_signup', {
    event_category: 'Engagement',
    event_label: 'Newsletter Signup',
    // Don't send email for privacy, just track the event
  });
}

/**
 * Track product views
 * @param productId - Product ID
 * @param productName - Product name
 * @param productCategory - Product category
 * @param price - Product price
 */
export function trackProductView(
  productId: string,
  productName: string,
  productCategory?: string,
  price?: number
): void {
  trackEvent('view_item', {
    event_category: 'E-commerce',
    event_label: productName,
    items: [
      {
        item_id: productId,
        item_name: productName,
        item_category: productCategory,
        price: price,
      },
    ],
  });
}

/**
 * Track outbound link clicks
 * @param url - URL of the outbound link
 * @param linkText - Text of the link
 */
export function trackOutboundLink(url: string, linkText?: string): void {
  trackEvent('click', {
    event_category: 'Outbound Link',
    event_label: linkText || url,
    outbound_url: url,
  });
}

/**
 * Set user properties for better segmentation
 * @param userId - User ID (if available)
 * @param userProperties - Additional user properties
 */
export function setUserProperties(userId?: string, userProperties?: Record<string, any>): void {
  if (!isAnalyticsAvailable()) {
    return;
  }

  window.gtag!('config', 'G-KVLS5RYWKC', {
    user_id: userId,
    ...userProperties,
  });
}
